import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const location = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);

  const toggleSubmenu = (path: string) => {
    setOpenSubmenus(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  const menuItems = useMemo(() => [
    {
      title: 'Dashboard',
      icon: 'bx-home-circle',
      path: '/dashboard',
      active: location.pathname === '/dashboard'
    },
    {
      title: 'Analytics',
      icon: 'bx-line-chart',
      path: '/analytics',
      active: location.pathname.startsWith('/analytics'),
      submenu: [
        {
          title: 'Customer map',
          icon: 'bx-map',
          path: '/analytics/customer-geography',
          active: location.pathname === '/analytics/customer-geography'
        }
      ]
    },
    {
      title: 'Users',
      icon: 'bx-user',
      path: '/users',
      active: location.pathname.startsWith('/users') || location.pathname.startsWith('/premium'),
      submenu: [
        {
          title: 'All Users',
          icon: 'bx-user-circle',
          path: '/users',
          active: location.pathname === '/users'
        },
        {
          title: 'Premium Users',
          icon: 'bx-user-check',
          path: '/premium/users',
          active: location.pathname === '/premium/users'
        }
      ]
    },
    {
      title: 'Flagged Users',
      icon: 'bx-flag',
      path: '/flagged-users',
      active: location.pathname === '/flagged-users'
    },
    {
      title: 'Support',
      icon: 'bx-support',
      path: '/support',
      active: location.pathname === '/support'
    },
    {
      title: 'Live Users',
      icon: 'bx-radio-circle-marked',
      path: '/live-users',
      active: location.pathname === '/live-users'
    },
    {
      title: 'App Assets',
      icon: 'bx-package',
      path: '/app-assets',
      active: location.pathname.startsWith('/app-assets'),
      submenu: [
        {
          title: 'Positions',
          icon: 'bx-location-plus',
          path: '/app-assets/positions',
          active: location.pathname === '/app-assets/positions'
        },
        {
          title: 'Body Types',
          icon: 'bx-body',
          path: '/app-assets/body-types',
          active: location.pathname === '/app-assets/body-types'
        },
        {
          title: 'Looking For',
          icon: 'bx-search-alt',
          path: '/app-assets/looking-for',
          active: location.pathname === '/app-assets/looking-for'
        },
        {
          title: 'Relationship Statuses',
          icon: 'bx-heart',
          path: '/app-assets/relationship-statuses',
          active: location.pathname === '/app-assets/relationship-statuses'
        },
        {
          title: 'Tags',
          icon: 'bx-purchase-tag',
          path: '/app-assets/tags',
          active: location.pathname === '/app-assets/tags'
        }
      ]
    },
    {
      title: 'Legal Docs',
      icon: 'bx-file-blank',
      path: '/legal-docs',
      active: location.pathname.startsWith('/legal-docs'),
      submenu: [
        {
          title: 'Privacy Policy',
          icon: 'bx-shield',
          path: '/legal-docs/privacy-policy',
          active: location.pathname === '/legal-docs/privacy-policy'
        },
        {
          title: 'Terms & Conditions',
          icon: 'bx-file',
          path: '/legal-docs/terms-conditions',
          active: location.pathname === '/legal-docs/terms-conditions'
        },
        {
          title: 'Community Guidelines',
          icon: 'bx-group',
          path: '/legal-docs/community-guidelines',
          active: location.pathname === '/legal-docs/community-guidelines'
        },
        {
          title: 'Child Safety Standards',
          icon: 'bx-shield-alt-2',
          path: '/legal-docs/child-safety-standards',
          active: location.pathname === '/legal-docs/child-safety-standards'
        }
      ]
    },
    {
      title: 'Website',
      icon: 'bx-globe',
      path: '/website',
      active: location.pathname.startsWith('/website'),
      submenu: [
        {
          title: 'Delete Account Requests',
          icon: 'bx-user-x',
          path: '/website/delete-account-requests',
          active: location.pathname === '/website/delete-account-requests'
        },
        {
          title: 'Contact Requests',
          icon: 'bx-message-square',
          path: '/website/contact-requests',
          active: location.pathname === '/website/contact-requests'
        },
        {
          title: 'Report Issue Requests',
          icon: 'bx-bug',
          path: '/website/report-issue-requests',
          active: location.pathname === '/website/report-issue-requests'
        }
      ]
    },
    {
      title: 'Communication',
      icon: 'bx-message-dots',
      path: '/communication',
      active: location.pathname.startsWith('/communication'),
      submenu: [
        {
          title: 'Send Email',
          icon: 'bx-envelope',
          path: '/communication/send-email',
          active: location.pathname === '/communication/send-email'
        },
        {
          title: 'Send Push Notification',
          icon: 'bx-bell',
          path: '/communication/send-push',
          active: location.pathname === '/communication/send-push'
        }
      ]
    },
    {
      title: 'Subscription Plans',
      icon: 'bx-credit-card',
      path: '/subscription-plans',
      active: location.pathname === '/subscription-plans'
    },
    {
      title: 'Rewards & Coins',
      icon: 'bx-gift',
      path: '/rewards',
      active: location.pathname.startsWith('/rewards'),
      submenu: [
        {
          title: 'Reward Settings',
          icon: 'bx-cog',
          path: '/rewards/settings',
          active: location.pathname === '/rewards/settings'
        },
        {
          title: 'Promo Codes',
          icon: 'bx-purchase-tag',
          path: '/rewards/promo-codes',
          active: location.pathname === '/rewards/promo-codes'
        }
      ]
    },
    {
      title: 'System',
      icon: 'bx-cog',
      path: '/system',
      active: location.pathname.startsWith('/system'),
      submenu: [
        {
          title: 'Google Console',
          icon: 'bx-google',
          path: '/system/google-console',
          active: location.pathname === '/system/google-console'
        },
        {
          title: 'App Credentials',
          icon: 'bx-shield-quarter',
          path: '/system/app-credentials',
          active: location.pathname === '/system/app-credentials'
        },
        {
          title: 'Payment Settings',
          icon: 'bx-credit-card',
          path: '/system/payment-settings',
          active: location.pathname === '/system/payment-settings'
        },
        {
          title: 'App Version Settings',
          icon: 'bx-mobile-alt',
          path: '/system/app-version-settings',
          active: location.pathname === '/system/app-version-settings'
        }
      ]
    }
  ], [location.pathname]);

  // Auto-open submenus when on child pages
  useEffect(() => {
    const currentPath = location.pathname;
    const newOpenSubmenus: string[] = [];
    
    menuItems.forEach(item => {
      if (item.submenu) {
        // Check if current path matches any submenu item
        const isOnSubmenuPage = item.submenu.some(subItem => subItem.path === currentPath);
        if (isOnSubmenuPage) {
          newOpenSubmenus.push(item.path);
        }
      }
    });
    
    setOpenSubmenus(newOpenSubmenus);
  }, [location.pathname, menuItems]);

  return (
    <>
      <style>{`
        .vertical-menu {
          background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
          overflow-y: auto;
          overflow-x: hidden;
          height: 100vh;
        }

        .vertical-menu::-webkit-scrollbar {
          display: none;
        }

        .vertical-menu {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        #sidebar-menu {
          padding-bottom: 20px;
        }

        #side-menu li a,
        #side-menu li button {
          padding: 12px 20px;
          margin: 4px 12px;
          border-radius: 8px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          font-weight: 500;
          font-size: 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          border: none;
          background: none;
          width: calc(100% - 24px);
          text-align: left;
          cursor: pointer;
          font-family: inherit;
        }

        #side-menu li a:hover,
        #side-menu li button:hover {
          background: rgba(0, 97, 117, 0.08) !important;
          color: #006175 !important;
          transform: translateX(4px);
        }

        #side-menu li a.active,
        #side-menu li button.active {
          background: linear-gradient(90deg, rgba(0, 97, 117, 0.15) 0%, rgba(0, 97, 117, 0.08) 100%) !important;
          color: #006175 !important;
          font-weight: 600;
          border-left: 3px solid #006175;
          padding-left: 17px;
        }

        #side-menu li a i.bx:first-child,
        #side-menu li button i.bx:first-child {
          font-size: 20px;
          margin-right: 12px;
          width: 24px;
          text-align: center;
        }

        #side-menu li a .bx-chevron-down,
        #side-menu li button .bx-chevron-down {
          font-size: 18px;
          transition: transform 0.3s ease;
          margin-left: auto;
        }

        #side-menu li a .bx-chevron-down.rotated,
        #side-menu li button .bx-chevron-down.rotated {
          transform: rotate(180deg);
        }

        #side-menu .sub-menu {
          margin: 0;
          padding: 8px 0;
          background: rgba(0, 97, 117, 0.02);
          border-radius: 8px;
          margin: 0 12px 8px 12px;
        }

        #side-menu .sub-menu li a {
          padding: 10px 20px 10px 52px;
          margin: 2px 8px;
          font-size: 13px;
          font-weight: 500;
          position: relative;
          text-decoration: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        #side-menu .sub-menu li a:hover {
          text-decoration: none;
        }

        #side-menu .sub-menu li a::before {
          content: '';
          position: absolute;
          left: 32px;
          top: 50%;
          transform: translateY(-50%);
          width: 6px;
          height: 6px;
          background: #6c757d;
          border-radius: 50%;
          opacity: 0.5;
          transition: all 0.3s ease;
        }

        #side-menu .sub-menu li a:hover::before {
          background: #006175;
          opacity: 1;
          transform: translateY(-50%) scale(1.3);
        }

        #side-menu .sub-menu li a.active {
          background: rgba(0, 97, 117, 0.1) !important;
          border-left: none;
          padding-left: 52px;
        }

        #side-menu .sub-menu li a.active::before {
          background: #006175;
          opacity: 1;
          width: 8px;
          height: 8px;
        }

        #side-menu .has-submenu > a {
          font-weight: 600;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        #side-menu .sub-menu[style*="display: block"] {
          animation: slideDown 0.3s ease;
        }
      `}</style>
      <div className={`vertical-menu ${isOpen ? 'show' : ''}`}>
        <div className="navbar-brand-box">
          <a href="/dashboard" className="logo logo-dark d-flex align-items-center justify-content-center" onClick={onClose}>
            <img
              src="/assets/images/header_logo.png"
              alt="Nyvro"
              height="40"
              style={{ width: 'auto', maxWidth: '180px', objectFit: 'contain' }}
            />
          </a>
        </div>

        <div className="h-100">
          <div id="sidebar-menu">
            <ul className="metismenu list-unstyled" id="side-menu">
              {menuItems.map((item, index) => (
                <li key={index} className={item.submenu ? 'has-submenu' : ''}>
                  {item.submenu ? (
                    <>
                      <button
                        className={`waves-effect ${item.active ? 'active' : ''}`}
                        onClick={() => toggleSubmenu(item.path)}
                      >
                        <i className={`bx ${item.icon}`}></i>
                        <span>{item.title}</span>
                        <i className={`bx bx-chevron-down ms-auto ${openSubmenus.includes(item.path) ? 'rotated' : ''}`}></i>
                      </button>
                          <ul className="sub-menu" style={{ display: openSubmenus.includes(item.path) ? 'block' : 'none' }}>
                            {item.submenu.map((subItem, subIndex) => (
                              <li key={subIndex}>
                                <Link
                                  to={subItem.path}
                                  className={`waves-effect ${subItem.active ? 'active' : ''}`}
                                  style={{
                                    color: subItem.active ? '#006175' : '#6c757d',
                                    backgroundColor: subItem.active ? '#00617510' : 'transparent'
                                  }}
                                  onClick={onClose}
                                >
                                  <span>{subItem.title}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`waves-effect ${item.active ? 'active' : ''}`}
                      style={{
                        color: item.active ? '#006175' : '#6c757d',
                        backgroundColor: item.active ? '#00617510' : 'transparent'
                      }}
                      onClick={onClose}
                    >
                      <i className={`bx ${item.icon}`}></i>
                      <span>{item.title}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;